import { ResourceOperations, ResourceTypes } from '../../authentication/resources';
import { useIsAllowed } from '../../authentication/use-is-allowed';
import { useFeatureToggles } from '../../feature-toggles/feature-toggles';
import { useTenantAddons, TenantAddonConstants } from '../../components/tenant-addons';
import type { IAdminAreaTabBar } from './admin-area';

function useCanSeeCitizenDeletionOrders() {
  const { isFeatureEnabled } = useFeatureToggles();
  return useIsAllowed([{ resourceType: ResourceTypes.CitizenDeletionOrder, resourceOperation: ResourceOperations.Read }]) && isFeatureEnabled('CitizenMassDeletionEnabled');
}

export function useCanSeeNotificationLog() {
  const { isFeatureEnabled } = useFeatureToggles();
  return useIsAllowed([{ resourceType: ResourceTypes.TransactionLog, resourceOperation: ResourceOperations.Read }]) && isFeatureEnabled('EnableAppNotificationLog');
}

export function useAdminTabsIsAllowed(): IAdminAreaTabBar {
  const { isFeatureEnabled } = useFeatureToggles();
  const { isTenantAddonEnabled } = useTenantAddons();

  const hasFasitAdministrationAccess = useIsAllowed([{ resourceType: ResourceTypes.FasitAdministration, resourceOperation: ResourceOperations.Access }]);

  const canSeeOrganizationResources = useIsAllowed([
    { resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.User, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.Team, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.SubJobcenter, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.CrmRoles, resourceOperation: ResourceOperations.Write },
    { resourceType: ResourceTypes.UserProfile, resourceOperation: ResourceOperations.Write },
    { resourceType: ResourceTypes.ExternalOperatorAgreement, resourceOperation: ResourceOperations.Write },
    { resourceType: ResourceTypes.SocialProviderUnit, resourceOperation: ResourceOperations.Read },
  ]);

  const canSeeOrganizationTab = hasFasitAdministrationAccess && canSeeOrganizationResources;

  const canSeeGeneralTab = useIsAllowed([
    { resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.GuideLinks, resourceOperation: ResourceOperations.Read },
  ]);

  const canSeeCitizensTab = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read }]);

  const canSeeCasesTab = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read }]);

  const canSeeCompanyTab = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read }]);

  const canSeeJobAndOffersTab = useIsAllowed([
    { resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read },
    { resourceType: ResourceTypes.QuestionnaireFeedback, resourceOperation: ResourceOperations.Read },
  ]);
  const canSeeOfferGroups = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Write }]);
  const canSeeForumFeedback = isTenantAddonEnabled(['ForumFeedback']);

  const canSeeTemplatesAndAppendixTab = useIsAllowed([{ resourceType: ResourceTypes.DocumentTemplate, resourceOperation: ResourceOperations.Write }]);

  const canSeeAuditTab = useIsAllowed([{ resourceType: ResourceTypes.AuditLog, resourceOperation: ResourceOperations.Read }]);

  const canSeeTransactionLogTab = useIsAllowed([{ resourceType: ResourceTypes.TransactionLog, resourceOperation: ResourceOperations.Read }]);

  const canSeeConfigurationLogTab = useIsAllowed([{ resourceType: ResourceTypes.BusinessConfiguration, resourceOperation: ResourceOperations.Read }]);

  const canSeeFilingTab =
    useIsAllowed([{ resourceType: ResourceTypes.SocialCases, resourceOperation: ResourceOperations.Write }]) && isTenantAddonEnabled([TenantAddonConstants.SocialMyndighed]);

  const canSeeCitizenDeletionTab =
    useIsAllowed([{ resourceType: ResourceTypes.Citizen, resourceOperation: ResourceOperations.Citizen.Delete }]) && isFeatureEnabled('CitizenDataDeletionEnabled');

  const canSeeCitizenDeletionOrders = useCanSeeCitizenDeletionOrders();

  const canSeeBillingAndCoPaymentTab =
    useIsAllowed([{ resourceType: ResourceTypes.CoPaymentType, resourceOperation: ResourceOperations.Read }]) && isTenantAddonEnabled([TenantAddonConstants.CoPayment]);

  const canSeeNotificationLogTab = useCanSeeNotificationLog();

  return {
    canSeeOrganizationTab,
    canSeeGeneralTab,
    canSeeCitizensTab,
    canSeeCasesTab,
    canSeeCompanyTab,
    canSeeJobAndOffersTab,
    canSeeOfferGroups,
    canSeeForumFeedback,
    canSeeTemplatesAndAppendixTab,
    canSeeAuditTab,
    canSeeTransactionLogTab,
    canSeeConfigurationLogTab,
    canSeeFilingTab,
    canSeeCitizenDeletionTab,
    canSeeCitizenDeletionOrders,
    canSeeBillingAndCoPaymentTab,
    canSeeNotificationLogTab,
  };
}
